import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Users, Ticket, DollarSign, TrendingUp, FileDown, Image as ImageIcon, Upload, Trash2, Building2, CheckCircle, XCircle, Clock, Heart, Briefcase, FolderPlus, FolderOpen, Star, Calendar, MapPin, AlertTriangle, RefreshCw, ChevronDown, ChevronUp, RotateCw, Pencil, Save, X, GripVertical, Mail, Download, Eye, Loader2, Filter, ArrowUpDown, ArrowUp, Gift } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("adminToken"));
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [stats, setStats] = useState(null);
  const [compradores, setCompradores] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [exalumnos, setExalumnos] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [ordenesSPEI, setOrdenesSPEI] = useState([]);
  const [eventosGeneracion, setEventosGeneracion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [creatingEvento, setCreatingEvento] = useState(false);
  const [resettingData, setResettingData] = useState(false);
  const [exalumnosCollapsed, setExalumnosCollapsed] = useState(true); // Collapsed by default
  const [editingAlbum, setEditingAlbum] = useState(null); // Album being edited
  const [editAlbumData, setEditAlbumData] = useState({ titulo: "", descripcion: "" });
  const [editingEventoImagen, setEditingEventoImagen] = useState(null); // Evento image being edited
  const [uploadingEventoImagen, setUploadingEventoImagen] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null); // Evento being edited (title/description)
  const [editEventoData, setEditEventoData] = useState({ titulo: "", descripcion: "", lugar: "" });
  const [newFoto, setNewFoto] = useState({ titulo: "", descripcion: "", url: "", album_id: "" });
  const [newAlbum, setNewAlbum] = useState({ titulo: "", descripcion: "" });
  const [newEvento, setNewEvento] = useState({ titulo: "", descripcion: "", fecha: "", lugar: "", imagen_url: "", album_id: "", destacado: false });
  const [filtroArea, setFiltroArea] = useState("Todas");
  const [draggedAlbum, setDraggedAlbum] = useState(null);
  const [selectedAlbumView, setSelectedAlbumView] = useState(null); // Album being viewed for photo management
  const [albumFotos, setAlbumFotos] = useState([]); // Photos in selected album
  
  // Directorio states
  const [directorioEntries, setDirectorioEntries] = useState([]);
  const [directorioSearch, setDirectorioSearch] = useState("");
  const [directorioCollapsed, setDirectorioCollapsed] = useState(true);
  const [editingDirectorio, setEditingDirectorio] = useState(null);
  const [newDirectorioEntry, setNewDirectorioEntry] = useState({
    nombre: "", tipo: "Exalumno 93-96", area: "", apodo: "", telefono: "", email: "", facebook: "", cumpleanos: ""
  });

  // Ticket details states
  const [expandedCompradorId, setExpandedCompradorId] = useState(null);
  const [compradorBoletos, setCompradorBoletos] = useState([]);
  const [loadingBoletos, setLoadingBoletos] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Filter states for Lista de Registros
  const [filtroTipoRegistro, setFiltroTipoRegistro] = useState("Todos");
  const [filtroEstatus, setFiltroEstatus] = useState("Todos");
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [sortColumn, setSortColumn] = useState("created_at"); // columna a ordenar
  const [sortDirection, setSortDirection] = useState("desc"); // asc o desc

  // Aportaciones en especie
  const [aportacionesEspecie, setAportacionesEspecie] = useState([]);

  // Scroll to top button
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (loggedIn) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      setToken(response.data.token);
      localStorage.setItem("adminToken", response.data.token);
      setLoggedIn(true);
      toast.success("Login exitoso");
    } catch (error) {
      toast.error("Contraseña incorrecta");
    }
  };

  const fetchData = async () => {
    if (!token) {
      setLoggedIn(false);
      return;
    }
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, compradoresRes, sponsorsRes, mensajesRes, exalumnosRes, fotosRes, ordenesSPEIRes, albumsRes, eventosRes, directorioStatsRes, directorioEntriesRes, aportacionesEspecieRes] = await Promise.all([
        axios.get(`${API}/estadisticas`, { headers }).catch((e) => {
          if (e.response?.status === 401) throw new Error('unauthorized');
          return { data: null };
        }),
        axios.get(`${API}/compradores`, { headers }).catch((e) => {
          if (e.response?.status === 401) throw new Error('unauthorized');
          return { data: [] };
        }),
        axios.get(`${API}/sponsors`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/contacto`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/exalumnos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/fotos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/ordenes-spei`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/albums`).catch(() => ({ data: [] })),
        axios.get(`${API}/eventos-generacion`).catch(() => ({ data: [] })),
        axios.get(`${API}/directorio/stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/directorio`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/aportaciones-especie`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      // Combine stats with directorio stats
      const combinedStats = {
        ...(statsRes.data || {}),
        directorio_total: directorioStatsRes.data?.total || 0,
        directorio_exalumnos: directorioStatsRes.data?.exalumnos || 0,
        directorio_maestros: directorioStatsRes.data?.maestros || 0,
        directorio_invitados: directorioStatsRes.data?.invitados || 0
      };
      setStats(combinedStats);
      setCompradores(compradoresRes.data || []);
      setSponsors(sponsorsRes.data || []);
      setMensajes(mensajesRes.data || []);
      setExalumnos(exalumnosRes.data || []);
      setFotos(fotosRes.data || []);
      setOrdenesSPEI(ordenesSPEIRes.data || []);
      setAlbums(albumsRes.data || []);
      setEventosGeneracion(eventosRes.data || []);
      setDirectorioEntries(directorioEntriesRes.data || []);
      setAportacionesEspecie(aportacionesEspecieRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error cargando datos");
      // Asegurar que los arrays estén inicializados aunque haya error
      setStats(null);
      setCompradores([]);
      setSponsors([]);
      setMensajes([]);
      setExalumnos([]);
      setFotos([]);
      setOrdenesSPEI([]);
      setAlbums([]);
      setEventosGeneracion([]);
      
      // Si es error de autorización, cerrar sesión
      if (error.message === 'unauthorized') {
        localStorage.removeItem("adminToken");
        setToken("");
        setLoggedIn(false);
        toast.error("Sesión expirada. Por favor inicia sesión nuevamente.");
      } else {
        toast.error("Error cargando datos. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!compradores || compradores.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    
    const csvContent = [
      ["Nombre", "Email", "Teléfono", "Tipo", "Cantidad Boletos", "Monto", "Fecha Registro"],
      ...compradores.map(c => [
        c.nombre_completo,
        c.email,
        c.telefono,
        c.tipo_registro || 'Boletos',
        c.cantidad_boletos || 0,
        c.monto || 0,
        new Date(c.created_at).toLocaleDateString()
      ])
    ]
      .map(row => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "registros.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Archivo descargado");
  };

  const marcarMensajeLeido = async (mensajeId) => {
    try {
      await axios.put(
        `${API}/contacto/${mensajeId}/leido`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Actualizar estado local
      setMensajes(prev =>
        prev.map(m => (m.id === mensajeId ? { ...m, leido: true } : m))
      );
      toast.success("Mensaje marcado como leído");
    } catch (error) {
      toast.error("Error al marcar mensaje");
    }
  };

  const eliminarMensaje = async (mensajeId) => {
    if (!window.confirm("¿Seguro que deseas eliminar este mensaje?")) return;
    try {
      await axios.delete(
        `${API}/admin/mensajes/${mensajeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMensajes(prev => prev.filter(m => m.id !== mensajeId));
      toast.success("Mensaje eliminado");
    } catch (error) {
      toast.error("Error al eliminar mensaje");
    }
  };

  const inicializarListaExalumnos = async () => {
    try {
      await axios.post(
        `${API}/exalumnos/inicializar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Lista de exalumnos inicializada");
      fetchData(); // Recargar datos
    } catch (error) {
      toast.error("Error al inicializar lista");
    }
  };

  const confirmarPagoSPEI = async (ordenId, tipoOrden) => {
    if (!window.confirm("¿Confirmas que recibiste el pago por transferencia SPEI?")) return;
    try {
      let endpoint = `${API}/admin/ordenes-spei/${ordenId}/confirmar`;
      let successMsg = "Pago confirmado";
      
      if (tipoOrden === 'sponsor') {
        endpoint = `${API}/admin/ordenes-spei-sponsor/${ordenId}/confirmar`;
        successMsg = "Pago de sponsor confirmado. Se envió recibo por correo.";
      } else if (tipoOrden === 'aportacion') {
        endpoint = `${API}/admin/ordenes-spei-aportacion/${ordenId}/confirmar`;
        successMsg = "Aportación confirmada. Se envió recibo por correo.";
      } else {
        // boletos
        const response = await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Pago confirmado. ${response.data.boletos_generados} boletos generados.`);
        fetchData();
        return;
      }
      
      await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(successMsg);
      fetchData();
    } catch (error) {
      toast.error("Error al confirmar pago");
    }
  };

  const cancelarOrdenSPEI = async (ordenId, tipoOrden) => {
    if (!window.confirm("¿Seguro que deseas cancelar esta orden?")) return;
    try {
      let endpoint = `${API}/admin/ordenes-spei/${ordenId}`;
      
      if (tipoOrden === 'sponsor') {
        endpoint = `${API}/admin/ordenes-spei-sponsor/${ordenId}`;
      } else if (tipoOrden === 'aportacion') {
        endpoint = `${API}/admin/ordenes-spei-aportacion/${ordenId}`;
      }
      
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Orden cancelada");
      fetchData();
    } catch (error) {
      toast.error("Error al cancelar orden");
    }
  };

  const eliminarRegistro = async (registroId, tipoRegistro) => {
    const mensajes = {
      'Boletos': '¿Seguro que deseas eliminar este comprador y sus boletos?',
      'Sponsor': '¿Seguro que deseas eliminar este sponsor?',
      'Aportación': '¿Seguro que deseas eliminar esta aportación?'
    };
    
    if (!window.confirm(mensajes[tipoRegistro] || '¿Seguro que deseas eliminar este registro?')) return;
    
    try {
      let endpoint = `${API}/admin/compradores/${registroId}`;
      
      if (tipoRegistro === 'Sponsor') {
        endpoint = `${API}/admin/sponsors/${registroId}`;
      } else if (tipoRegistro === 'Aportación') {
        endpoint = `${API}/admin/donaciones/${registroId}`;
      }
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Registro eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar registro");
    }
  };

  // Toggle expanded view for comprador details
  const toggleCompradorDetalles = async (compradorId) => {
    if (expandedCompradorId === compradorId) {
      setExpandedCompradorId(null);
      setCompradorBoletos([]);
      return;
    }
    
    setExpandedCompradorId(compradorId);
    setLoadingBoletos(true);
    
    try {
      const response = await axios.get(`${API}/boletos/comprador/${compradorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompradorBoletos(response.data || []);
    } catch (error) {
      console.error("Error fetching boletos:", error);
      setCompradorBoletos([]);
    } finally {
      setLoadingBoletos(false);
    }
  };

  // Resend tickets via email
  const reenviarEmailBoletos = async (compradorId, email) => {
    if (!window.confirm(`¿Reenviar todos los boletos a ${email}?`)) return;
    
    setResendingEmail(compradorId);
    try {
      const response = await axios.post(
        `${API}/boletos/reenviar/${compradorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Boletos reenviados correctamente");
    } catch (error) {
      console.error("Error resending email:", error);
      const errorMsg = error.response?.data?.detail || "Error al reenviar boletos";
      toast.error(errorMsg);
    } finally {
      setResendingEmail(null);
    }
  };

  // Download all tickets as merged PDF
  const descargarPdfBoletos = async (compradorId, nombreComprador) => {
    setDownloadingPdf(compradorId);
    try {
      const response = await axios.get(
        `${API}/boletos/pdf-todos/${compradorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `boletos_${nombreComprador.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado correctamente");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      const errorMsg = error.response?.data?.detail || "Error al descargar PDF";
      toast.error(errorMsg);
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Expanded details state for sponsors/aportaciones
  const [expandedSponsorId, setExpandedSponsorId] = useState(null);
  const [expandedAportacionId, setExpandedAportacionId] = useState(null);
  const [sendingEmailSponsor, setSendingEmailSponsor] = useState(null);
  const [sendingEmailAportacion, setSendingEmailAportacion] = useState(null);
  const [downloadingPdfSponsor, setDownloadingPdfSponsor] = useState(null);
  const [downloadingPdfAportacion, setDownloadingPdfAportacion] = useState(null);

  // Toggle sponsor details
  const toggleSponsorDetalles = (sponsorId) => {
    setExpandedSponsorId(expandedSponsorId === sponsorId ? null : sponsorId);
  };

  // Toggle aportacion details
  const toggleAportacionDetalles = (aportacionId) => {
    setExpandedAportacionId(expandedAportacionId === aportacionId ? null : aportacionId);
  };

  // Send confirmation email to sponsor
  const enviarEmailSponsor = async (sponsor) => {
    if (!window.confirm(`¿Enviar email de confirmación a ${sponsor.email}?`)) return;
    
    setSendingEmailSponsor(sponsor.id);
    try {
      await axios.post(
        `${API}/admin/sponsor/${sponsor.id}/enviar-confirmacion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email de confirmación enviado");
    } catch (error) {
      console.error("Error sending sponsor email:", error);
      toast.error(error.response?.data?.detail || "Error al enviar email");
    } finally {
      setSendingEmailSponsor(null);
    }
  };

  // Send thank you email to aportacion
  const enviarEmailAportacion = async (aportacion) => {
    if (!window.confirm(`¿Enviar email de agradecimiento a ${aportacion.email}?`)) return;
    
    setSendingEmailAportacion(aportacion.id);
    try {
      await axios.post(
        `${API}/admin/aportacion/${aportacion.id}/enviar-agradecimiento`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email de agradecimiento enviado");
    } catch (error) {
      console.error("Error sending aportacion email:", error);
      toast.error(error.response?.data?.detail || "Error al enviar email");
    } finally {
      setSendingEmailAportacion(null);
    }
  };

  // Download PDF for sponsor
  const descargarPdfSponsor = async (sponsor) => {
    setDownloadingPdfSponsor(sponsor.id);
    try {
      const response = await axios.get(
        `${API}/admin/sponsor/${sponsor.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const nombreArchivo = `certificado_${(sponsor.nombre_negocio || sponsor.nombre_completo || 'sponsor').replace(/\s+/g, '_')}.pdf`;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado correctamente");
    } catch (error) {
      console.error("Error downloading sponsor PDF:", error);
      toast.error(error.response?.data?.detail || "Error al descargar PDF");
    } finally {
      setDownloadingPdfSponsor(null);
    }
  };

  // Download PDF for aportacion
  const descargarPdfAportacion = async (aportacion) => {
    setDownloadingPdfAportacion(aportacion.id);
    try {
      const response = await axios.get(
        `${API}/admin/aportacion/${aportacion.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const nombreArchivo = `recibo_${(aportacion.nombre || aportacion.nombre_completo || 'aportacion').replace(/\s+/g, '_')}.pdf`;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado correctamente");
    } catch (error) {
      console.error("Error downloading aportacion PDF:", error);
      toast.error(error.response?.data?.detail || "Error al descargar PDF");
    } finally {
      setDownloadingPdfAportacion(null);
    }
  };

  // Filter and sort compradores
  const getFilteredCompradores = () => {
    if (!compradores || compradores.length === 0) return [];
    
    let filtered = [...compradores];
    
    // Filter by tipo
    if (filtroTipoRegistro !== "Todos") {
      filtered = filtered.filter(c => c.tipo_registro === filtroTipoRegistro);
    }
    
    // Filter by estatus
    if (filtroEstatus !== "Todos") {
      filtered = filtered.filter(c => c.estatus === filtroEstatus);
    }
    
    // Filter by date range
    if (filtroFechaDesde) {
      const desde = new Date(filtroFechaDesde);
      desde.setHours(0, 0, 0, 0);
      filtered = filtered.filter(c => new Date(c.created_at) >= desde);
    }
    
    if (filtroFechaHasta) {
      const hasta = new Date(filtroFechaHasta);
      hasta.setHours(23, 59, 59, 999);
      filtered = filtered.filter(c => new Date(c.created_at) <= hasta);
    }
    
    // Sort by column
    filtered.sort((a, b) => {
      let valA, valB;
      
      switch (sortColumn) {
        case "nombre_completo":
          valA = (a.nombre_completo || "").toLowerCase();
          valB = (b.nombre_completo || "").toLowerCase();
          break;
        case "email":
          valA = (a.email || "").toLowerCase();
          valB = (b.email || "").toLowerCase();
          break;
        case "tipo_registro":
          valA = (a.tipo_registro || "").toLowerCase();
          valB = (b.tipo_registro || "").toLowerCase();
          break;
        case "estatus":
          valA = (a.estatus || "").toLowerCase();
          valB = (b.estatus || "").toLowerCase();
          break;
        case "metodo_pago":
          valA = (a.metodo_pago || "").toLowerCase();
          valB = (b.metodo_pago || "").toLowerCase();
          break;
        case "created_at":
        default:
          valA = new Date(a.created_at);
          valB = new Date(b.created_at);
          break;
      }
      
      // Compare
      if (sortColumn === "created_at") {
        return sortDirection === "asc" ? valA - valB : valB - valA;
      } else {
        const comparison = valA.localeCompare ? valA.localeCompare(valB) : (valA < valB ? -1 : valA > valB ? 1 : 0);
        return sortDirection === "asc" ? comparison : -comparison;
      }
    });
    
    return filtered;
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Get sort indicator for column header
  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const limpiarFiltros = () => {
    setFiltroTipoRegistro("Todos");
    setFiltroEstatus("Todos");
    setFiltroFechaDesde("");
    setFiltroFechaHasta("");
    setSortColumn("created_at");
    setSortDirection("desc");
  };

  // Aportaciones en especie functions
  const actualizarEstadoAportacionEspecie = async (id, nuevoEstado) => {
    try {
      await axios.put(
        `${API}/admin/aportaciones-especie/${id}/estado`,
        { estado: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Estado actualizado a "${nuevoEstado}"`);
      fetchData();
    } catch (error) {
      toast.error("Error actualizando estado");
    }
  };

  const eliminarAportacionEspecie = async (id) => {
    if (!window.confirm("¿Eliminar esta aportación en especie?")) return;
    try {
      await axios.delete(`${API}/admin/aportaciones-especie/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aportación eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error eliminando aportación");
    }
  };

  const agregarFoto = async (e) => {
    e.preventDefault();
    if (!newFoto.titulo || !newFoto.url) {
      toast.error("Título y URL son requeridos");
      return;
    }
    setUploadingFoto(true);
    try {
      await axios.post(
        `${API}/fotos`,
        {
          titulo: newFoto.titulo,
          descripcion: newFoto.descripcion,
          url: newFoto.url,
          tipo: "galeria"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Foto agregada correctamente");
      setNewFoto({ titulo: "", descripcion: "", url: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al agregar foto");
    } finally {
      setUploadingFoto(false);
    }
  };

  const eliminarFoto = async (fotoId) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta foto?")) return;
    try {
      await axios.delete(`${API}/fotos/${fotoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Foto eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar foto");
    }
  };

  const crearAlbum = async (e) => {
    e.preventDefault();
    if (!newAlbum.titulo.trim()) {
      toast.error("El título del álbum es requerido");
      return;
    }
    setCreatingAlbum(true);
    try {
      await axios.post(
        `${API}/albums?titulo=${encodeURIComponent(newAlbum.titulo)}&descripcion=${encodeURIComponent(newAlbum.descripcion)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Álbum creado exitosamente");
      setNewAlbum({ titulo: "", descripcion: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear álbum");
    } finally {
      setCreatingAlbum(false);
    }
  };

  const eliminarAlbum = async (albumId) => {
    if (!window.confirm("¿Seguro que deseas eliminar este álbum? Las fotos no se eliminarán, quedarán sin álbum asignado.")) return;
    try {
      await axios.delete(`${API}/albums/${albumId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Álbum eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar álbum");
    }
  };

  const establecerPortada = async (albumId, fotoUrl) => {
    try {
      await axios.put(
        `${API}/albums/${albumId}?portada_url=${encodeURIComponent(fotoUrl)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Portada del álbum actualizada");
      fetchData();
    } catch (error) {
      toast.error("Error al establecer portada");
    }
  };

  // ============ ALBUM PHOTO MANAGEMENT ============
  const openAlbumView = (album) => {
    setSelectedAlbumView(album);
    // Filter photos that belong to this album
    const fotosDelAlbum = fotos.filter(f => f.album_id === album.id);
    setAlbumFotos(fotosDelAlbum);
  };

  const closeAlbumView = () => {
    setSelectedAlbumView(null);
    setAlbumFotos([]);
  };

  const quitarFotoDeAlbum = async (fotoId) => {
    try {
      await axios.put(
        `${API}/fotos/${fotoId}/album?album_id=`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setAlbumFotos(prev => prev.filter(f => f.id !== fotoId));
      setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, album_id: null } : f));
      toast.success("Foto removida del álbum");
    } catch (error) {
      toast.error("Error al remover foto");
    }
  };

  const moverFotoAAlbum = async (fotoId, nuevoAlbumId) => {
    try {
      await axios.post(
        `${API}/fotos/${fotoId}/album?album_id=${nuevoAlbumId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      if (selectedAlbumView && nuevoAlbumId !== selectedAlbumView.id) {
        setAlbumFotos(prev => prev.filter(f => f.id !== fotoId));
      }
      setFotos(prev => prev.map(f => f.id === fotoId ? { ...f, album_id: nuevoAlbumId } : f));
      toast.success("Foto movida a otro álbum");
    } catch (error) {
      toast.error("Error al mover foto");
    }
  };

  // ============ EVENTOS DE LA GENERACIÓN ============
  const crearEvento = async (e) => {
    e.preventDefault();
    if (!newEvento.titulo.trim() || !newEvento.fecha || !newEvento.lugar.trim()) {
      toast.error("Título, fecha y lugar son requeridos");
      return;
    }
    setCreatingEvento(true);
    try {
      let url = `${API}/eventos-generacion?titulo=${encodeURIComponent(newEvento.titulo)}&descripcion=${encodeURIComponent(newEvento.descripcion)}&fecha=${encodeURIComponent(newEvento.fecha)}&lugar=${encodeURIComponent(newEvento.lugar)}&destacado=${newEvento.destacado}`;
      if (newEvento.imagen_url) {
        url += `&imagen_url=${encodeURIComponent(newEvento.imagen_url)}`;
      }
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Evento creado exitosamente");
      setNewEvento({ titulo: "", descripcion: "", fecha: "", lugar: "", imagen_url: "", album_id: "", destacado: false });
      fetchData();
    } catch (error) {
      toast.error("Error al crear evento");
    } finally {
      setCreatingEvento(false);
    }
  };

  const eliminarEvento = async (eventoId) => {
    if (!window.confirm("¿Seguro que deseas eliminar este evento?")) return;
    try {
      await axios.delete(`${API}/eventos-generacion/${eventoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evento eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar evento");
    }
  };

  const vincularAlbumEvento = async (eventoId, albumId) => {
    try {
      await axios.put(
        `${API}/eventos-generacion/${eventoId}?album_id=${encodeURIComponent(albumId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Álbum vinculado al evento");
      fetchData();
    } catch (error) {
      toast.error("Error al vincular álbum");
    }
  };

  const actualizarImagenEvento = async (eventoId, file) => {
    if (!file) return;
    
    setUploadingEventoImagen(true);
    try {
      // Get Cloudinary signature
      const signRes = await axios.get(
        `${API}/cloudinary/signature?folder=evento`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { signature, timestamp, cloud_name, api_key, folder } = signRes.data;
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", api_key);
      formData.append("timestamp", timestamp);
      formData.append("signature", signature);
      formData.append("folder", folder);
      
      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        formData
      );
      
      // Update event with new image URL
      await axios.put(
        `${API}/eventos-generacion/${eventoId}?imagen_url=${encodeURIComponent(uploadRes.data.secure_url)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state instead of fetchData to prevent page jump
      setEventosGeneracion(prev => 
        prev.map(e => e.id === eventoId ? { ...e, imagen_url: uploadRes.data.secure_url } : e)
      );
      
      toast.success("Imagen del evento actualizada");
      setEditingEventoImagen(null);
    } catch (error) {
      console.error("Error uploading event image:", error);
      toast.error("Error al subir imagen");
    } finally {
      setUploadingEventoImagen(false);
    }
  };

  // ============ EDITAR EVENTO (TITULO/DESCRIPCION) ============
  const startEditingEvento = (evento) => {
    setEditingEvento(evento.id);
    setEditEventoData({
      titulo: evento.titulo,
      descripcion: evento.descripcion || "",
      lugar: evento.lugar || ""
    });
  };

  const cancelEditingEvento = () => {
    setEditingEvento(null);
    setEditEventoData({ titulo: "", descripcion: "", lugar: "" });
  };

  const guardarEdicionEvento = async (eventoId) => {
    if (!editEventoData.titulo.trim()) {
      toast.error("El título es requerido");
      return;
    }
    try {
      await axios.put(
        `${API}/eventos-generacion/${eventoId}?titulo=${encodeURIComponent(editEventoData.titulo)}&descripcion=${encodeURIComponent(editEventoData.descripcion)}&lugar=${encodeURIComponent(editEventoData.lugar)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setEventosGeneracion(prev =>
        prev.map(e => e.id === eventoId ? { ...e, ...editEventoData } : e)
      );
      toast.success("Evento actualizado");
      setEditingEvento(null);
    } catch (error) {
      toast.error("Error al actualizar evento");
    }
  };

  // ============ DIRECTORIO CRUD ============
  const agregarDirectorioEntry = async () => {
    if (!newDirectorioEntry.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    try {
      await axios.post(`${API}/directorio`, newDirectorioEntry, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Registro agregado al directorio");
      setNewDirectorioEntry({
        nombre: "", tipo: "Exalumno 93-96", area: "", apodo: "", telefono: "", email: "", facebook: "", cumpleanos: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Error al agregar registro");
    }
  };

  const actualizarDirectorioEntry = async (entryId) => {
    if (!editingDirectorio?.nombre?.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    try {
      await axios.put(`${API}/directorio/${entryId}`, editingDirectorio, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Registro actualizado");
      setEditingDirectorio(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar registro");
    }
  };

  const eliminarDirectorioEntry = async (entryId, nombre) => {
    if (!window.confirm(`¿Eliminar a "${nombre}" del directorio?`)) return;
    try {
      await axios.delete(`${API}/directorio/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Registro eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar registro");
    }
  };

  const filteredDirectorio = directorioEntries.filter(entry => {
    if (!directorioSearch) return true;
    const term = directorioSearch.toLowerCase();
    return entry.nombre?.toLowerCase().includes(term) || 
           entry.apodo?.toLowerCase().includes(term) ||
           entry.area?.toLowerCase().includes(term);
  });

  // ============ RESET DATA FOR PRODUCTION ============
  const resetDataForProduction = async () => {
    const pinIngresado = window.prompt("⚠️ ATENCIÓN: Esto eliminará TODOS los datos de ventas.\n\nIngresa el PIN de 4 dígitos para confirmar:");
    
    if (pinIngresado !== "3100") {
      if (pinIngresado !== null) {
        toast.error("PIN incorrecto. Reset cancelado.");
      }
      return;
    }
    
    setResettingData(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset-data?keep_frevo_sponsor=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const results = response.data.results;
      toast.success(`Reset completado: ${results.compradores_deleted} compradores, ${results.boletos_deleted} boletos, ${results.ordenes_spei_deleted} órdenes SPEI eliminados`);
      fetchData();
    } catch (error) {
      toast.error("Error al resetear datos");
    } finally {
      setResettingData(false);
    }
  };

  // ============ ROTATE PHOTO ============
  const rotarFoto = async (fotoId, currentRotation) => {
    const newRotation = ((currentRotation || 0) + 90) % 360;
    try {
      await axios.put(
        `${API}/fotos/${fotoId}/rotate?rotation=${newRotation}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state instead of fetchData to prevent page jump
      setFotos(prevFotos => 
        prevFotos.map(foto => 
          foto.id === fotoId ? { ...foto, rotation: newRotation } : foto
        )
      );
      toast.success("Foto rotada");
    } catch (error) {
      toast.error("Error al rotar foto");
    }
  };

  // ============ EDIT ALBUM ============
  const startEditingAlbum = (album) => {
    setEditingAlbum(album.id);
    setEditAlbumData({ titulo: album.titulo, descripcion: album.descripcion || "" });
  };

  const cancelEditingAlbum = () => {
    setEditingAlbum(null);
    setEditAlbumData({ titulo: "", descripcion: "" });
  };

  const saveAlbumEdit = async (albumId) => {
    if (!editAlbumData.titulo.trim()) {
      toast.error("El título es requerido");
      return;
    }
    try {
      await axios.put(
        `${API}/albums/${albumId}?titulo=${encodeURIComponent(editAlbumData.titulo)}&descripcion=${encodeURIComponent(editAlbumData.descripcion)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Álbum actualizado");
      setEditingAlbum(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar álbum");
    }
  };

  // ============ DRAG & DROP ALBUMS ============
  const handleDragStart = (e, album, index) => {
    setDraggedAlbum({ album, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (!draggedAlbum || draggedAlbum.index === targetIndex) {
      setDraggedAlbum(null);
      return;
    }

    // Reorder albums locally first (no fetchData to prevent page jump)
    const newAlbums = [...albums];
    const [movedAlbum] = newAlbums.splice(draggedAlbum.index, 1);
    newAlbums.splice(targetIndex, 0, movedAlbum);
    
    // Update local state immediately
    setAlbums(newAlbums);
    setDraggedAlbum(null);

    // Update order in backend (don't call fetchData)
    try {
      const albumIds = newAlbums.map(a => a.id);
      await axios.put(
        `${API}/albums/reorder?${albumIds.map(id => `album_ids=${id}`).join('&')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Orden de álbumes actualizado");
    } catch (error) {
      console.error("Error reordering:", error);
      toast.error("Error al reordenar álbumes");
      // Revert on error by fetching fresh data
      fetchData();
    }
  };

  const handleDragEnd = () => {
    setDraggedAlbum(null);
  };


  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setLoggedIn(false);
    setToken("");
    navigate("/");
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-warm-bone flex items-center justify-center py-12">
        <div className="glass-card rounded-3xl p-8 md:p-12 max-w-md w-full mx-4">
          <h1 className="text-3xl font-bold text-zuno-wine mb-6 text-center">
            Panel de Administración
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Contraseña</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa la contraseña"
                required
                data-testid="password-input"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" data-testid="login-btn">
              Ingresar
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-bone py-12">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex justify-between items-center mb-8">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2" size={20} />
            Volver
          </Button>
          <Button onClick={handleLogout} variant="outline" data-testid="logout-btn">
            Cerrar Sesión
          </Button>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine mb-8">
          Panel de Administración
        </h1>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-zuno-wine"></div>
          </div>
        ) : (
          <>
            {/* Estadísticas */}
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              <Card className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <Users className="h-7 w-7 text-blue-600" />
                </div>
                <p className="text-xs text-slate-600 mb-1">Compradores</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.total_compradores || 0}</p>
              </Card>

              <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <Ticket className="h-7 w-7 text-green-600" />
                </div>
                <p className="text-xs text-slate-600 mb-1">Boletos Vendidos</p>
                <p className="text-2xl font-bold text-green-600">{stats?.total_boletos_vendidos || 0}</p>
              </Card>

              <Card className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="h-7 w-7 text-purple-600" />
                </div>
                <p className="text-xs text-slate-600 mb-1">Check-ins</p>
                <p className="text-2xl font-bold text-purple-600">{stats?.total_checkins || 0}</p>
              </Card>

              <Card className="p-5 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="h-7 w-7 text-amber-600" />
                </div>
                <p className="text-xs text-slate-600 mb-1">Recaudado Total</p>
                <p className="text-2xl font-bold text-amber-600">
                  ${(stats?.total_recaudado_general || 0).toLocaleString()}
                </p>
              </Card>

              <Card className="p-5 bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
                <div className="flex items-center justify-between mb-2">
                  <Briefcase className="h-7 w-7 text-rose-600" />
                </div>
                <p className="text-xs text-slate-600 mb-1">$ Sponsors</p>
                <p className="text-2xl font-bold text-rose-600">
                  ${(stats?.total_sponsors || 0).toLocaleString()}
                </p>
              </Card>

              <Card className="p-5 bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                <div className="flex items-center justify-between mb-2">
                  <Heart className="h-7 w-7 text-teal-600" />
                </div>
                <p className="text-xs text-slate-600 mb-1">$ Aportaciones</p>
                <p className="text-2xl font-bold text-teal-600">
                  ${(stats?.total_aportaciones || 0).toLocaleString()}
                </p>
              </Card>
            </div>

            {/* Detalle de Recaudación */}
            <Card className="p-6 mb-8">
              <h2 className="text-2xl font-bold text-zuno-wine mb-4">Detalle de Ingresos</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Venta de Boletos:</span>
                  <span className="font-semibold text-lg">${(stats?.total_recaudado_boletos || 0).toLocaleString()} MXN</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Sponsors:</span>
                  <span className="font-semibold text-lg">${(stats?.total_sponsors || 0).toLocaleString()} MXN</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Aportaciones:</span>
                  <span className="font-semibold text-lg">${(stats?.total_aportaciones || 0).toLocaleString()} MXN</span>
                </div>
                <div className="border-t-2 border-zuno-wine pt-3 flex justify-between items-center">
                  <span className="text-xl font-bold text-zuno-wine">Total:</span>
                  <span className="text-2xl font-bold text-zuno-wine">
                    ${(stats?.total_recaudado_general || 0).toLocaleString()} MXN
                  </span>
                </div>
              </div>
            </Card>

            {/* Órdenes SPEI Pendientes */}
            <div className="glass-card rounded-3xl p-8 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-zuno-wine flex items-center gap-3">
                  <Building2 className="h-7 w-7" />
                  Órdenes SPEI Pendientes
                </h2>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-semibold">
                  {ordenesSPEI.filter(o => o.estado === 'pendiente').length} pendientes
                </span>
              </div>

              {ordenesSPEI.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No hay órdenes SPEI</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ordenesSPEI.map((orden) => (
                    <Card 
                      key={orden.id} 
                      className={`p-4 ${orden.estado === 'completado' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
                      data-testid={`orden-spei-${orden.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {orden.estado === 'completado' ? (
                              <CheckCircle className="text-green-600" size={20} />
                            ) : (
                              <Clock className="text-amber-600" size={20} />
                            )}
                            <span className="font-bold text-lg">{orden.referencia}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              orden.estado === 'completado' ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'
                            }`}>
                              {orden.estado === 'completado' ? 'Confirmado' : 'Pendiente'}
                            </span>
                            {/* Badge de tipo de orden */}
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              orden.tipo_orden === 'sponsor' ? 'bg-purple-200 text-purple-800' :
                              orden.tipo_orden === 'aportacion' ? 'bg-teal-200 text-teal-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {orden.tipo_orden === 'sponsor' ? 'Sponsor' :
                               orden.tipo_orden === 'aportacion' ? 'Aportación' : 'Boletos'}
                            </span>
                          </div>
                          <p className="text-slate-700"><strong>{orden.nombre || orden.nombre_negocio}</strong></p>
                          <p className="text-sm text-slate-600">{orden.email || orden.contacto_email} {orden.telefono && `• ${orden.telefono}`}</p>
                          <p className="text-sm text-slate-500">
                            {orden.tipo_orden === 'sponsor' && orden.paquete && `Paquete: ${orden.paquete} • `}
                            {orden.cantidad_boletos && `${orden.cantidad_boletos} boleto(s) • `}
                            Creado: {new Date(orden.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold text-zuno-wine">${(orden.total || orden.monto || 0).toLocaleString()} MXN</span>
                          {orden.estado === 'pendiente' && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => confirmarPagoSPEI(orden.id, orden.tipo_orden)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                size="sm"
                                data-testid={`confirmar-spei-${orden.id}`}
                              >
                                <CheckCircle size={16} className="mr-1" />
                                Confirmar Pago
                              </Button>
                              <Button
                                onClick={() => cancelarOrdenSPEI(orden.id, orden.tipo_orden)}
                                variant="destructive"
                                size="sm"
                                data-testid={`cancelar-spei-${orden.id}`}
                              >
                                <XCircle size={16} className="mr-1" />
                                Cancelar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Lista de Compradores */}
            <div className="glass-card rounded-3xl p-8">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-zuno-wine">Lista de Registros</h2>
                  <Button
                    onClick={exportarExcel}
                    variant="outline"
                    data-testid="export-btn"
                  >
                    <FileDown className="mr-2" size={18} />
                    Exportar CSV
                  </Button>
                </div>
                
                {/* Filters Row */}
                <div className="flex flex-wrap gap-3 items-end bg-slate-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Filter size={18} className="text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">Filtros:</span>
                  </div>
                  
                  {/* Tipo Filter */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-slate-500">Tipo</label>
                    <select
                      value={filtroTipoRegistro}
                      onChange={(e) => setFiltroTipoRegistro(e.target.value)}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-zuno-wine/20 focus:border-zuno-wine"
                      data-testid="filter-tipo"
                    >
                      <option value="Todos">Todos</option>
                      <option value="Boletos">Boletos</option>
                      <option value="Sponsor">Sponsor</option>
                      <option value="Aportación">Aportación</option>
                    </select>
                  </div>
                  
                  {/* Estatus Filter */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-slate-500">Estatus</label>
                    <select
                      value={filtroEstatus}
                      onChange={(e) => setFiltroEstatus(e.target.value)}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-zuno-wine/20 focus:border-zuno-wine"
                      data-testid="filter-estatus"
                    >
                      <option value="Todos">Todos</option>
                      <option value="Confirmado">Confirmado</option>
                      <option value="Pendiente">Pendiente</option>
                    </select>
                  </div>
                  
                  {/* Date From Filter */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-slate-500">Desde</label>
                    <input
                      type="date"
                      value={filtroFechaDesde}
                      onChange={(e) => setFiltroFechaDesde(e.target.value)}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-zuno-wine/20 focus:border-zuno-wine"
                      data-testid="filter-fecha-desde"
                    />
                  </div>
                  
                  {/* Date To Filter */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-slate-500">Hasta</label>
                    <input
                      type="date"
                      value={filtroFechaHasta}
                      onChange={(e) => setFiltroFechaHasta(e.target.value)}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-zuno-wine/20 focus:border-zuno-wine"
                      data-testid="filter-fecha-hasta"
                    />
                  </div>
                  
                  {/* Clear Filters Button */}
                  <Button
                    onClick={limpiarFiltros}
                    variant="ghost"
                    size="sm"
                    className="text-slate-600 hover:text-zuno-wine"
                    data-testid="clear-filters"
                  >
                    <X size={16} className="mr-1" />
                    Limpiar
                  </Button>
                </div>
                
                {/* Results count */}
                <div className="text-sm text-slate-600">
                  Mostrando <span className="font-semibold">{getFilteredCompradores().length}</span> de <span className="font-semibold">{compradores?.length || 0}</span> registros
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th 
                        onClick={() => handleSort("nombre_completo")} 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        title="Ordenar por nombre"
                      >
                        <div className="flex items-center gap-1">
                          Nombre
                          <span className="text-zuno-wine">{getSortIndicator("nombre_completo")}</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort("email")} 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        title="Ordenar por email"
                      >
                        <div className="flex items-center gap-1">
                          Email
                          <span className="text-zuno-wine">{getSortIndicator("email")}</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort("tipo_registro")} 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        title="Ordenar por tipo"
                      >
                        <div className="flex items-center gap-1">
                          Tipo
                          <span className="text-zuno-wine">{getSortIndicator("tipo_registro")}</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort("estatus")} 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        title="Ordenar por estatus"
                      >
                        <div className="flex items-center gap-1">
                          Estatus
                          <span className="text-zuno-wine">{getSortIndicator("estatus")}</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort("metodo_pago")} 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        title="Ordenar por método de pago"
                      >
                        <div className="flex items-center gap-1">
                          Método Pago
                          <span className="text-zuno-wine">{getSortIndicator("metodo_pago")}</span>
                        </div>
                      </th>
                      <th>Referencias / Boletos</th>
                      <th 
                        onClick={() => handleSort("created_at")} 
                        className="cursor-pointer hover:bg-slate-100 select-none"
                        title="Ordenar por fecha"
                      >
                        <div className="flex items-center gap-1">
                          Fecha
                          <span className="text-zuno-wine">{getSortIndicator("created_at")}</span>
                        </div>
                      </th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredCompradores().length > 0 ? (
                      getFilteredCompradores().map((comprador, index) => (
                        <React.Fragment key={comprador.id}>
                          <tr data-testid={`buyer-row-${index}`} className={expandedCompradorId === comprador.id ? 'bg-slate-50' : ''}>
                            <td>{comprador.nombre_completo}</td>
                            <td className="text-sm">{comprador.email}</td>
                            <td>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                comprador.tipo_registro === 'Boletos' ? 'bg-green-100 text-green-800' :
                                comprador.tipo_registro === 'Sponsor' ? 'bg-purple-100 text-purple-800' :
                                comprador.tipo_registro === 'Aportación' ? 'bg-teal-100 text-teal-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {comprador.tipo_registro || 'Boletos'}
                              </span>
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                comprador.estatus === 'Confirmado' ? 'bg-green-100 text-green-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>
                                {comprador.estatus || 'Pendiente'}
                              </span>
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                comprador.metodo_pago === 'Stripe' ? 'bg-indigo-100 text-indigo-800' :
                                comprador.metodo_pago === 'SPEI' ? 'bg-blue-100 text-blue-800' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {comprador.metodo_pago || '-'}
                              </span>
                            </td>
                            <td>
                              {comprador.tipo_registro === 'Boletos' && comprador.boletos && comprador.boletos.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {comprador.boletos.slice(0, 3).map((ref, i) => (
                                    <span key={`${comprador.id}-boleto-${ref}`} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                                      {ref}
                                    </span>
                                  ))}
                                  {comprador.boletos.length > 3 && (
                                    <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded">
                                      +{comprador.boletos.length - 3} más
                                    </span>
                                  )}
                                </div>
                              ) : comprador.referencia_spei ? (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                  {comprador.referencia_spei}
                                </span>
                              ) : comprador.monto ? (
                                <span className="text-slate-600 text-sm">${comprador.monto.toLocaleString()}</span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="text-sm">{new Date(comprador.created_at).toLocaleDateString()}</td>
                            <td>
                              <div className="flex gap-1 flex-wrap">
                                {/* === BOLETOS ACTIONS === */}
                                {comprador.tipo_registro === 'Boletos' && comprador.estatus === 'Confirmado' && (
                                  <>
                                    <Button
                                      onClick={() => toggleCompradorDetalles(comprador.id)}
                                      variant="outline"
                                      size="sm"
                                      title="Ver detalles"
                                      data-testid={`view-details-${index}`}
                                      className="px-2"
                                    >
                                      {expandedCompradorId === comprador.id ? <ChevronUp size={16} /> : <Eye size={16} />}
                                    </Button>
                                    <Button
                                      onClick={() => reenviarEmailBoletos(comprador.id, comprador.email)}
                                      variant="outline"
                                      size="sm"
                                      title="Reenviar boletos por email"
                                      disabled={resendingEmail === comprador.id}
                                      data-testid={`resend-email-${index}`}
                                      className="px-2"
                                    >
                                      {resendingEmail === comprador.id ? (
                                        <Loader2 size={16} className="animate-spin" />
                                      ) : (
                                        <Mail size={16} />
                                      )}
                                    </Button>
                                    <Button
                                      onClick={() => descargarPdfBoletos(comprador.id, comprador.nombre_completo)}
                                      variant="outline"
                                      size="sm"
                                      title="Descargar boletos PDF"
                                      disabled={downloadingPdf === comprador.id}
                                      data-testid={`download-pdf-${index}`}
                                      className="px-2"
                                    >
                                      {downloadingPdf === comprador.id ? (
                                        <Loader2 size={16} className="animate-spin" />
                                      ) : (
                                        <Download size={16} />
                                      )}
                                    </Button>
                                  </>
                                )}
                                
                                {/* === SPONSOR ACTIONS === */}
                                {comprador.tipo_registro === 'Sponsor' && (
                                  <>
                                    <Button
                                      onClick={() => toggleSponsorDetalles(comprador.id)}
                                      variant="outline"
                                      size="sm"
                                      title="Ver detalles"
                                      className="px-2"
                                    >
                                      {expandedSponsorId === comprador.id ? <ChevronUp size={16} /> : <Eye size={16} />}
                                    </Button>
                                    {comprador.estatus === 'Confirmado' && (
                                      <>
                                        <Button
                                          onClick={() => enviarEmailSponsor(comprador)}
                                          variant="outline"
                                          size="sm"
                                          title="Enviar confirmación por email"
                                          disabled={sendingEmailSponsor === comprador.id}
                                          className="px-2"
                                        >
                                          {sendingEmailSponsor === comprador.id ? (
                                            <Loader2 size={16} className="animate-spin" />
                                          ) : (
                                            <Mail size={16} />
                                          )}
                                        </Button>
                                        <Button
                                          onClick={() => descargarPdfSponsor(comprador)}
                                          variant="outline"
                                          size="sm"
                                          title="Descargar certificado PDF"
                                          disabled={downloadingPdfSponsor === comprador.id}
                                          data-testid={`download-pdf-sponsor-${comprador.id}`}
                                          className="px-2"
                                        >
                                          {downloadingPdfSponsor === comprador.id ? (
                                            <Loader2 size={16} className="animate-spin" />
                                          ) : (
                                            <Download size={16} />
                                          )}
                                        </Button>
                                      </>
                                    )}
                                  </>
                                )}
                                
                                {/* === APORTACION ACTIONS === */}
                                {comprador.tipo_registro === 'Aportación' && (
                                  <>
                                    <Button
                                      onClick={() => toggleAportacionDetalles(comprador.id)}
                                      variant="outline"
                                      size="sm"
                                      title="Ver detalles"
                                      className="px-2"
                                    >
                                      {expandedAportacionId === comprador.id ? <ChevronUp size={16} /> : <Eye size={16} />}
                                    </Button>
                                    {comprador.estatus === 'Confirmado' && (
                                      <>
                                        <Button
                                          onClick={() => enviarEmailAportacion(comprador)}
                                          variant="outline"
                                          size="sm"
                                          title="Enviar agradecimiento por email"
                                          disabled={sendingEmailAportacion === comprador.id}
                                          className="px-2"
                                        >
                                          {sendingEmailAportacion === comprador.id ? (
                                            <Loader2 size={16} className="animate-spin" />
                                          ) : (
                                            <Mail size={16} />
                                          )}
                                        </Button>
                                        <Button
                                          onClick={() => descargarPdfAportacion(comprador)}
                                          variant="outline"
                                          size="sm"
                                          title="Descargar recibo PDF"
                                          disabled={downloadingPdfAportacion === comprador.id}
                                          data-testid={`download-pdf-aportacion-${comprador.id}`}
                                          className="px-2"
                                        >
                                          {downloadingPdfAportacion === comprador.id ? (
                                            <Loader2 size={16} className="animate-spin" />
                                          ) : (
                                            <Download size={16} />
                                          )}
                                        </Button>
                                      </>
                                    )}
                                  </>
                                )}
                                
                                {/* Delete Button - For all types */}
                                <Button
                                  onClick={() => eliminarRegistro(comprador.id, comprador.tipo_registro)}
                                  variant="destructive"
                                  size="sm"
                                  data-testid={`delete-buyer-${index}`}
                                  className="px-2"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Expanded Details Row */}
                          {expandedCompradorId === comprador.id && comprador.tipo_registro === 'Boletos' && (
                            <tr className="bg-slate-50 border-t-0">
                              <td colSpan="8" className="p-4">
                                {loadingBoletos ? (
                                  <div className="flex items-center justify-center py-4">
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    <span className="text-slate-600">Cargando detalles...</span>
                                  </div>
                                ) : (
                                  <div className="space-y-4">
                                    {/* Buyer Info Summary */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-4 rounded-lg border">
                                      <div>
                                        <p className="text-xs text-slate-500">Teléfono</p>
                                        <p className="font-medium">{comprador.telefono || '-'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500">Tipo Comprador</p>
                                        <p className="font-medium">{comprador.tipo_comprador || '-'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500">Cantidad Boletos</p>
                                        <p className="font-medium">{comprador.cantidad_boletos || compradorBoletos.length}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500">Total Boletos Generados</p>
                                        <p className="font-medium text-green-600">{compradorBoletos.length}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Asistentes Adicionales */}
                                    {comprador.asistentes_adicionales && comprador.asistentes_adicionales.length > 0 && (
                                      <div className="bg-white p-4 rounded-lg border">
                                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                                          <Users size={16} className="mr-2" />
                                          Asistentes Adicionales ({comprador.asistentes_adicionales.length})
                                        </h4>
                                        <div className="grid gap-2">
                                          {comprador.asistentes_adicionales.map((asistente, i) => (
                                            <div key={`${comprador.id}-asistente-${asistente.nombre}-${i}`} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                                              <span className="w-6 h-6 rounded-full bg-zuno-wine text-white text-xs flex items-center justify-center">
                                                {i + 1}
                                              </span>
                                              <span className="font-medium">{asistente.nombre}</span>
                                              <span className={`px-2 py-0.5 rounded text-xs ${
                                                asistente.tipo === 'Maestro' ? 'bg-blue-100 text-blue-800' :
                                                asistente.tipo === 'Exalumno 93-96' ? 'bg-green-100 text-green-800' :
                                                'bg-purple-100 text-purple-800'
                                              }`}>
                                                {asistente.tipo}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Boletos Generados */}
                                    {compradorBoletos.length > 0 && (
                                      <div className="bg-white p-4 rounded-lg border">
                                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                                          <Ticket size={16} className="mr-2" />
                                          Boletos Generados ({compradorBoletos.length})
                                        </h4>
                                        <div className="overflow-x-auto">
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="bg-slate-100">
                                                <th className="px-3 py-2 text-left">No. Boleto</th>
                                                <th className="px-3 py-2 text-left">Asistente</th>
                                                <th className="px-3 py-2 text-left">Tipo</th>
                                                <th className="px-3 py-2 text-left">Precio</th>
                                                <th className="px-3 py-2 text-left">Estado</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {compradorBoletos.map((boleto, i) => (
                                                <tr key={boleto.numero_boleto} className="border-b border-slate-100">
                                                  <td className="px-3 py-2 font-mono text-xs">{boleto.numero_boleto}</td>
                                                  <td className="px-3 py-2">{boleto.nombre_asistente}</td>
                                                  <td className="px-3 py-2">
                                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                                      boleto.tipo_asistente === 'Maestro' ? 'bg-blue-100 text-blue-800' :
                                                      boleto.tipo_asistente === 'Exalumno 93-96' ? 'bg-green-100 text-green-800' :
                                                      'bg-purple-100 text-purple-800'
                                                    }`}>
                                                      {boleto.tipo_asistente}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2">
                                                    {boleto.precio === 0 ? (
                                                      <span className="text-green-600 font-medium">Gratis</span>
                                                    ) : (
                                                      <span>${boleto.precio?.toLocaleString()}</span>
                                                    )}
                                                  </td>
                                                  <td className="px-3 py-2">
                                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                                      boleto.estado === 'usado' ? 'bg-green-100 text-green-800' :
                                                      boleto.estado === 'cancelado' ? 'bg-red-100 text-red-800' :
                                                      'bg-amber-100 text-amber-800'
                                                    }`}>
                                                      {boleto.estado === 'usado' ? 'Check-in ✓' : 
                                                       boleto.estado === 'cancelado' ? 'Cancelado' : 'Activo'}
                                                    </span>
                                                    {boleto.checkin_timestamp && (
                                                      <span className="text-xs text-slate-500 ml-2">
                                                        {new Date(boleto.checkin_timestamp).toLocaleString('es-MX')}
                                                      </span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {compradorBoletos.length === 0 && !loadingBoletos && (
                                      <div className="text-center py-4 text-slate-500">
                                        No hay boletos generados para este comprador
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          
                          {/* Expanded Details Row - SPONSOR */}
                          {expandedSponsorId === comprador.id && comprador.tipo_registro === 'Sponsor' && (
                            <tr className="bg-purple-50 border-t-0">
                              <td colSpan="8" className="p-4">
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-4 rounded-lg border">
                                    <div>
                                      <p className="text-xs text-slate-500">Negocio</p>
                                      <p className="font-medium">{comprador.nombre_negocio || comprador.nombre_completo}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-500">Paquete</p>
                                      <p className="font-medium">{comprador.paquete || '-'}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-500">Monto</p>
                                      <p className="font-medium text-green-600">${comprador.monto?.toLocaleString() || 0}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-500">Teléfono</p>
                                      <p className="font-medium">{comprador.telefono || '-'}</p>
                                    </div>
                                  </div>
                                  {comprador.descripcion && (
                                    <div className="bg-white p-4 rounded-lg border">
                                      <p className="text-xs text-slate-500 mb-1">Descripción del Negocio</p>
                                      <p className="text-sm">{comprador.descripcion}</p>
                                    </div>
                                  )}
                                  {comprador.logo_url && (
                                    <div className="bg-white p-4 rounded-lg border">
                                      <p className="text-xs text-slate-500 mb-2">Logo</p>
                                      <img src={comprador.logo_url} alt="Logo" className="h-20 object-contain" />
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                          
                          {/* Expanded Details Row - APORTACION */}
                          {expandedAportacionId === comprador.id && comprador.tipo_registro === 'Aportación' && (
                            <tr className="bg-teal-50 border-t-0">
                              <td colSpan="8" className="p-4">
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-4 rounded-lg border">
                                    <div>
                                      <p className="text-xs text-slate-500">Nombre</p>
                                      <p className="font-medium">{comprador.nombre_completo}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-500">Email</p>
                                      <p className="font-medium">{comprador.email}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-500">Monto</p>
                                      <p className="font-medium text-green-600">${comprador.monto?.toLocaleString() || 0}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-500">Teléfono</p>
                                      <p className="font-medium">{comprador.telefono || '-'}</p>
                                    </div>
                                  </div>
                                  {comprador.mensaje && (
                                    <div className="bg-white p-4 rounded-lg border">
                                      <p className="text-xs text-slate-500 mb-1">Mensaje</p>
                                      <p className="text-sm">{comprador.mensaje}</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-8 text-slate-600">
                          {compradores && compradores.length > 0 
                            ? "No hay registros que coincidan con los filtros" 
                            : "No hay registros"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Lista de Sponsors */}
            <div className="glass-card rounded-3xl p-8 mt-8">
              <h2 className="text-2xl font-bold text-zuno-wine mb-6">Patrocinadores</h2>
              
              {!sponsors || sponsors.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No hay patrocinadores registrados</p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sponsors.map((sponsor, index) => (
                    <Card key={sponsor.id} className="p-6" data-testid={`sponsor-${index}`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          sponsor.paquete === "Limón" ? "bg-yellow-100 text-yellow-800" :
                          sponsor.paquete === "Guille" ? "bg-purple-100 text-purple-800" :
                          "bg-blue-100 text-blue-800"
                        }`}>
                          {sponsor.paquete}
                        </span>
                        <span className="text-lg font-bold text-zuno-wine">
                          ${sponsor.monto.toLocaleString()}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">
                        {sponsor.nombre_negocio}
                      </h3>
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Giro:</strong> {sponsor.giro}
                      </p>
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Dirección:</strong> {sponsor.direccion}
                      </p>
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Contacto:</strong> {sponsor.contacto_nombre}
                      </p>
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Email:</strong> {sponsor.contacto_email}
                      </p>
                      <p className="text-sm text-slate-600">
                        <strong>Teléfono:</strong> {sponsor.contacto_telefono}
                      </p>
                      {sponsor.logo_url && (
                        <div className="mt-4">
                          <img src={sponsor.logo_url} alt={sponsor.nombre_negocio} className="max-h-20 object-contain" />
                        </div>
                      )}
                      <div className={`mt-4 px-2 py-1 rounded text-center text-xs ${
                        sponsor.estado_pago === 'pagado' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {sponsor.estado_pago === 'pagado' ? 'Pagado' : 'Pendiente'}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Aportaciones en Especie */}
            <div className="glass-card rounded-3xl p-8 mt-8" data-testid="aportaciones-especie-section">
              <h2 className="text-2xl font-bold text-zuno-wine mb-6 flex items-center">
                <Gift className="mr-3" size={28} />
                Aportaciones en Especie ({aportacionesEspecie?.length || 0})
              </h2>
              
              {!aportacionesEspecie || aportacionesEspecie.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No hay aportaciones en especie registradas</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Email</th>
                        <th>Teléfono</th>
                        <th>Descripción de Aportación</th>
                        <th>Fecha</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aportacionesEspecie.map((aportacion, index) => (
                        <tr key={aportacion.id} data-testid={`aportacion-especie-${index}`}>
                          <td className="font-medium">{aportacion.nombre}</td>
                          <td className="text-sm">{aportacion.email}</td>
                          <td className="text-sm">{aportacion.telefono || '-'}</td>
                          <td className="max-w-xs">
                            <p className="text-sm text-slate-700 line-clamp-2">{aportacion.descripcion}</p>
                          </td>
                          <td className="text-sm">{new Date(aportacion.created_at).toLocaleDateString()}</td>
                          <td>
                            <select
                              value={aportacion.estado}
                              onChange={(e) => actualizarEstadoAportacionEspecie(aportacion.id, e.target.value)}
                              className={`px-2 py-1 rounded text-xs font-semibold border-0 cursor-pointer ${
                                aportacion.estado === 'confirmado' ? 'bg-green-100 text-green-800' :
                                aportacion.estado === 'recibido' ? 'bg-blue-100 text-blue-800' :
                                aportacion.estado === 'cancelado' ? 'bg-red-100 text-red-800' :
                                'bg-amber-100 text-amber-800'
                              }`}
                            >
                              <option value="pendiente">Pendiente</option>
                              <option value="confirmado">Confirmado</option>
                              <option value="recibido">Recibido</option>
                              <option value="cancelado">Cancelado</option>
                            </select>
                          </td>
                          <td>
                            <Button
                              onClick={() => eliminarAportacionEspecie(aportacion.id)}
                              variant="destructive"
                              size="sm"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Mensajes de Contacto */}
            <div className="glass-card rounded-3xl p-8 mt-8">
              <h2 className="text-2xl font-bold text-zuno-wine mb-6">
                Mensajes de Contacto ({mensajes ? mensajes.filter(m => !m.leido).length : 0} sin leer)
              </h2>
              
              {!mensajes || mensajes.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No hay mensajes</p>
              ) : (
                <div className="space-y-4">
                  {mensajes.map((mensaje, index) => (
                    <Card
                      key={mensaje.id}
                      className={`p-6 ${!mensaje.leido ? "border-2 border-zuno-wine bg-slate-50" : ""}`}
                      data-testid={`mensaje-${index}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">
                            {mensaje.nombre}
                          </h3>
                          <p className="text-sm text-slate-600">{mensaje.email}</p>
                          {mensaje.telefono && (
                            <p className="text-sm text-slate-600">{mensaje.telefono}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs text-slate-500">
                            {new Date(mensaje.created_at).toLocaleDateString("es-MX", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </span>
                          {!mensaje.leido && (
                            <Button
                              onClick={() => marcarMensajeLeido(mensaje.id)}
                              size="sm"
                              variant="outline"
                              data-testid={`mark-read-${index}`}
                            >
                              Marcar como leído
                            </Button>
                          )}
                          <Button
                            onClick={() => eliminarMensaje(mensaje.id)}
                            size="sm"
                            variant="destructive"
                            data-testid={`delete-msg-${index}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{mensaje.mensaje}</p>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Lista de Exalumnos */}
            <div className="glass-card rounded-3xl p-8 mt-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold text-zuno-wine">
                    Lista de Exalumnos Generación 93-96 
                    {exalumnos && exalumnos.length > 0 && (
                      <span className="text-lg font-normal text-slate-600 ml-2">
                        ({exalumnos.filter(e => e.tiene_boleto).length}/{exalumnos.length} con boleto)
                      </span>
                    )}
                  </h2>
                  {exalumnos && exalumnos.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExalumnosCollapsed(!exalumnosCollapsed)}
                      className="flex items-center gap-1"
                      data-testid="toggle-exalumnos-btn"
                    >
                      {exalumnosCollapsed ? (
                        <>
                          <ChevronDown size={16} />
                          Mostrar Lista
                        </>
                      ) : (
                        <>
                          <ChevronUp size={16} />
                          Ocultar Lista
                        </>
                      )}
                    </Button>
                  )}
                </div>
                {(!exalumnos || exalumnos.length === 0) && (
                  <Button
                    onClick={inicializarListaExalumnos}
                    variant="outline"
                    data-testid="init-alumni-btn"
                  >
                    Inicializar Lista
                  </Button>
                )}
              </div>
              
              {!exalumnos || exalumnos.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-slate-600 mb-4">La lista de exalumnos no está inicializada</p>
                  <p className="text-sm text-slate-500">Haz clic en "Inicializar Lista" para cargar los 194 exalumnos</p>
                </div>
              ) : exalumnosCollapsed ? (
                <div className="text-center py-6 text-slate-500">
                  <p>Lista colapsada. Haz clic en "Mostrar Lista" para ver los exalumnos.</p>
                </div>
              ) : (
                <>
                  {/* Filtros por área */}
                  <div className="mb-6 flex flex-wrap gap-2">
                    <span className="text-sm font-semibold text-slate-700">Filtrar por área:</span>
                    {["Todas", "Medicina", "Humanidades", "Arquitectura", "Ingenieria", "Administracion"].map(area => (
                      <Button
                        key={area}
                        variant={filtroArea === area ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFiltroArea(area)}
                        className={filtroArea === area ? "bg-zuno-wine text-white" : ""}
                      >
                        {area}
                      </Button>
                    ))}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Área</th>
                          <th className="text-center">Boleto</th>
                          <th>Fecha Compra</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exalumnos
                          .filter(e => filtroArea === "Todas" || e.area === filtroArea)
                          .map((exalumno, index) => (
                          <tr 
                            key={exalumno.id} 
                            data-testid={`alumni-row-${index}`}
                            className={exalumno.tiene_boleto ? "bg-green-50" : ""}
                          >
                            <td>{exalumno.nombre}</td>
                            <td>{exalumno.area}</td>
                            <td className="text-center">
                              {exalumno.tiene_boleto ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                  ✓ YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                                  NO
                                </span>
                              )}
                            </td>
                            <td>
                              {exalumno.fecha_compra ? 
                                new Date(exalumno.fecha_compra).toLocaleDateString("es-MX", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric"
                                }) : 
                                "-"
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 text-center text-sm text-slate-600">
                    <p>
                      <strong>Nota:</strong> Los exalumnos marcados con "YES" ya compraron su boleto. 
                      El sistema los detecta automáticamente cuando compran como "Exalumno 93-96".
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Gestión de Fotos y Álbumes */}
            <div className="glass-card rounded-3xl p-8 mt-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-zuno-wine flex items-center gap-3">
                  <ImageIcon className="h-7 w-7" />
                  Gestión de Fotos y Álbumes
                </h2>
              </div>

              {/* Crear Nuevo Álbum */}
              <Card className="p-6 mb-6 bg-purple-50 border-purple-200">
                <h3 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                  <FolderPlus size={20} />
                  Crear Nuevo Álbum
                </h3>
                <form onSubmit={crearAlbum} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Título del Álbum *</label>
                      <Input
                        type="text"
                        value={newAlbum.titulo}
                        onChange={(e) => setNewAlbum({ ...newAlbum, titulo: e.target.value })}
                        placeholder="Ej: Reunión 2024"
                        data-testid="album-titulo-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Descripción</label>
                      <Input
                        type="text"
                        value={newAlbum.descripcion}
                        onChange={(e) => setNewAlbum({ ...newAlbum, descripcion: e.target.value })}
                        placeholder="Ej: Fotos del evento de reencuentro"
                        data-testid="album-descripcion-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={creatingAlbum}
                    data-testid="crear-album-btn"
                  >
                    {creatingAlbum ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Creando...
                      </>
                    ) : (
                      <>
                        <FolderPlus size={16} className="mr-2" />
                        Crear Álbum
                      </>
                    )}
                  </Button>
                </form>
              </Card>

              {/* Lista de Álbumes - Draggable */}
              {albums.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FolderOpen size={20} />
                    Álbumes ({albums.length})
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">Arrastra los álbumes para cambiar su orden</p>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {albums.map((album, index) => (
                      <Card 
                        key={album.id} 
                        className={`overflow-hidden border-2 border-purple-200 transition-all ${draggedAlbum?.album.id === album.id ? 'opacity-50 scale-95 ring-2 ring-purple-500' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        data-testid={`album-admin-${album.id}`}
                      >
                        <div className="aspect-video relative bg-gradient-to-br from-purple-100 to-purple-200">
                          {album.portada_url ? (
                            <img
                              src={album.portada_url}
                              alt={album.titulo}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FolderOpen className="h-12 w-12 text-purple-400" />
                            </div>
                          )}
                          {/* Drag handle - only this is draggable */}
                          <div 
                            className="absolute top-2 left-2 bg-black/50 rounded p-1 text-white cursor-grab active:cursor-grabbing hover:bg-black/70"
                            draggable
                            onDragStart={(e) => handleDragStart(e, album, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <GripVertical size={16} />
                          </div>
                          <div className="absolute top-2 right-2 flex gap-1">
                            <Button
                              variant="secondary"
                              size="sm"
                              className="bg-blue-500 hover:bg-blue-600 text-white"
                              onClick={(e) => { e.stopPropagation(); startEditingAlbum(album); }}
                              data-testid={`editar-album-${album.id}`}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); eliminarAlbum(album.id); }}
                              data-testid={`eliminar-album-${album.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <div className="p-3">
                          {editingAlbum === album.id ? (
                            <div className="space-y-2">
                              <Input
                                value={editAlbumData.titulo}
                                onChange={(e) => setEditAlbumData({ ...editAlbumData, titulo: e.target.value })}
                                placeholder="Título"
                                className="text-sm"
                                data-testid={`edit-album-titulo-${album.id}`}
                              />
                              <Input
                                value={editAlbumData.descripcion}
                                onChange={(e) => setEditAlbumData({ ...editAlbumData, descripcion: e.target.value })}
                                placeholder="Descripción"
                                className="text-sm"
                                data-testid={`edit-album-desc-${album.id}`}
                              />
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 flex-1"
                                  onClick={() => saveAlbumEdit(album.id)}
                                  data-testid={`save-album-${album.id}`}
                                >
                                  <Save size={14} className="mr-1" /> Guardar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEditingAlbum}
                                  data-testid={`cancel-album-${album.id}`}
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <h4 className="font-semibold text-sm text-purple-900">{album.titulo}</h4>
                              {album.descripcion && (
                                <p className="text-xs text-slate-500 truncate">{album.descripcion}</p>
                              )}
                              <div className="flex justify-between items-center mt-1">
                                <p className="text-xs text-purple-600">{album.fotos_count || 0} fotos</p>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-6 px-2 text-purple-700 hover:text-purple-900"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    e.preventDefault();
                                    openAlbumView(album); 
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  draggable={false}
                                  data-testid={`ver-album-${album.id}`}
                                >
                                  Ver fotos →
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Modal para ver fotos del álbum */}
              {selectedAlbumView && (
                <div className="mb-8 bg-purple-50 rounded-2xl p-4 border-2 border-purple-300">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-purple-900">📁 {selectedAlbumView.titulo}</h3>
                      <p className="text-sm text-purple-700">{albumFotos.length} fotos en este álbum</p>
                    </div>
                    <Button variant="outline" onClick={closeAlbumView} className="border-purple-400">
                      <X size={20} className="mr-1" /> Cerrar
                    </Button>
                  </div>
                  
                  {albumFotos.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FolderOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p>Este álbum no tiene fotos</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                      {albumFotos.map((foto) => (
                        <div key={foto.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                            <img
                              src={foto.url}
                              alt={foto.titulo}
                              className="w-full h-full object-cover"
                              style={{ transform: `rotate(${foto.rotation || 0}deg)` }}
                            />
                          </div>
                          {/* Botón de eliminar rápido */}
                          <button
                            onClick={() => eliminarFoto(foto.id)}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            title="Eliminar foto permanentemente"
                            data-testid={`delete-foto-${foto.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg p-1">
                            <p className="text-white text-xs text-center truncate w-full mb-1">{foto.titulo}</p>
                            <select
                              className="text-xs bg-white rounded px-1 py-0.5 w-full"
                              value=""
                              onChange={(e) => {
                                if (e.target.value === "quitar") {
                                  quitarFotoDeAlbum(foto.id);
                                } else if (e.target.value) {
                                  moverFotoAAlbum(foto.id, e.target.value);
                                }
                              }}
                            >
                              <option value="">Mover a...</option>
                              <option value="quitar">❌ Quitar del álbum</option>
                              {albums.filter(a => a.id !== selectedAlbumView.id).map(a => (
                                <option key={a.id} value={a.id}>📁 {a.titulo}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Formulario para agregar foto con Cloudinary */}
              <Card className="p-6 mb-6 bg-slate-50">
                <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Upload size={20} />
                  Subir Nueva Foto (individual o múltiples)
                </h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Título Base *</label>
                      <Input
                        type="text"
                        value={newFoto.titulo}
                        onChange={(e) => setNewFoto({ ...newFoto, titulo: e.target.value })}
                        placeholder="Ej: Graduación"
                        data-testid="foto-titulo-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Si subes varias fotos, se nombrarán: Graduación (1), Graduación (2), etc.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Descripción</label>
                      <Input
                        type="text"
                        value={newFoto.descripcion}
                        onChange={(e) => setNewFoto({ ...newFoto, descripcion: e.target.value })}
                        placeholder="Ej: Foto del anuario 1996"
                        data-testid="foto-descripcion-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Álbum (opcional)</label>
                      <select
                        value={newFoto.album_id}
                        onChange={(e) => setNewFoto({ ...newFoto, album_id: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zuno-wine"
                        data-testid="foto-album-select"
                      >
                        <option value="">Sin álbum</option>
                        {albums.map((album) => (
                          <option key={album.id} value={album.id}>
                            {album.titulo}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Seleccionar Imagen(es) *</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length === 0) return;
                        
                        if (!newFoto.titulo.trim()) {
                          toast.error("Ingresa un título primero");
                          return;
                        }
                        
                        setUploadingFoto(true);
                        const baseTitle = newFoto.titulo.trim();
                        let successCount = 0;
                        let errorCount = 0;
                        
                        try {
                          for (let i = 0; i < files.length; i++) {
                            const file = files[i];
                            // Generate title with consecutive number if multiple files
                            const photoTitle = files.length > 1 
                              ? `${baseTitle} (${i + 1})` 
                              : baseTitle;
                            
                            try {
                              // Obtener firma de Cloudinary
                              const signRes = await axios.get(
                                `${API}/cloudinary/signature?folder=galeria`,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              const { signature, timestamp, cloud_name, api_key, folder } = signRes.data;
                              
                              // Subir a Cloudinary
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("api_key", api_key);
                              formData.append("timestamp", timestamp);
                              formData.append("signature", signature);
                              formData.append("folder", folder);
                              
                              const uploadRes = await axios.post(
                                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                                formData
                              );
                              
                              // Guardar en base de datos con album_id
                              let uploadUrl = `${API}/fotos/upload?titulo=${encodeURIComponent(photoTitle)}&descripcion=${encodeURIComponent(newFoto.descripcion)}&url=${encodeURIComponent(uploadRes.data.secure_url)}`;
                              if (newFoto.album_id) {
                                uploadUrl += `&album_id=${encodeURIComponent(newFoto.album_id)}`;
                              }
                              
                              await axios.post(
                                uploadUrl,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              
                              successCount++;
                            } catch (err) {
                              console.error(`Error subiendo foto ${i + 1}:`, err);
                              errorCount++;
                            }
                          }
                          
                          if (successCount > 0) {
                            toast.success(`${successCount} foto(s) subida(s) exitosamente${errorCount > 0 ? `, ${errorCount} con error` : ''}`);
                            setNewFoto({ titulo: "", descripcion: "", url: "", album_id: "" });
                            fetchData();
                          } else {
                            toast.error("Error al subir las fotos");
                          }
                          e.target.value = "";
                        } catch (error) {
                          console.error("Error subiendo fotos:", error);
                          toast.error("Error al subir las fotos");
                        } finally {
                          setUploadingFoto(false);
                        }
                      }}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-zuno-wine file:text-white hover:file:bg-zuno-wine/90 cursor-pointer"
                      data-testid="foto-file-input"
                      disabled={uploadingFoto}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Puedes seleccionar múltiples imágenes (Ctrl+Click o Shift+Click). Formatos: JPG, PNG, GIF, WebP
                    </p>
                  </div>
                  {uploadingFoto && (
                    <div className="flex items-center gap-2 text-zuno-wine">
                      <div className="animate-spin h-5 w-5 border-2 border-zuno-wine border-t-transparent rounded-full"></div>
                      <span>Subiendo foto...</span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Lista de fotos actuales */}
              <h3 className="text-lg font-semibold text-slate-700 mb-4">Todas las Fotos ({fotos.length})</h3>
              
              {fotos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ImageIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No hay fotos en la galería.</p>
                  <p className="text-sm">Agrega fotos usando el formulario de arriba.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                  {fotos.map((foto) => {
                    const fotoAlbum = albums.find(a => a.id === foto.album_id);
                    const rotation = foto.rotation || 0;
                    return (
                      <Card key={foto.id} className="overflow-hidden" data-testid={`foto-card-${foto.id}`}>
                        <div className="aspect-square relative overflow-hidden">
                          <img
                            src={foto.url}
                            alt={foto.titulo}
                            className="w-full h-full object-cover"
                            style={{ transform: `rotate(${rotation}deg)` }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300?text=Error+cargando+imagen";
                            }}
                          />
                          <div className="absolute top-1 right-1 flex gap-0.5">
                            <Button
                              variant="secondary"
                              size="sm"
                              className="bg-blue-500 hover:bg-blue-600 text-white h-6 w-6 p-0"
                              onClick={() => rotarFoto(foto.id, rotation)}
                              title="Rotar 90°"
                              data-testid={`rotar-foto-${foto.id}`}
                            >
                              <RotateCw size={12} />
                            </Button>
                            {foto.album_id && (
                              <Button
                                variant="secondary"
                                size="sm"
                                className="bg-amber-500 hover:bg-amber-600 text-white h-6 w-6 p-0"
                                onClick={() => establecerPortada(foto.album_id, foto.url)}
                                title="Establecer como portada del álbum"
                                data-testid={`set-portada-${foto.id}`}
                              >
                                <Star size={12} />
                              </Button>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => eliminarFoto(foto.id)}
                              data-testid={`eliminar-foto-${foto.id}`}
                            >
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </div>
                        <div className="p-1.5">
                          <h4 className="font-semibold text-xs truncate">{foto.titulo}</h4>
                          {fotoAlbum ? (
                            <p className="text-xs text-purple-600 truncate flex items-center gap-0.5">
                              <FolderOpen size={10} />
                              {fotoAlbum.titulo}
                            </p>
                          ) : (
                            <p className="text-xs text-slate-400">Sin álbum</p>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <h4 className="font-semibold text-amber-800 mb-2">💡 Instrucciones</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• <strong>Álbumes:</strong> Crea álbumes para organizar las fotos por evento o tema</li>
                  <li>• <strong>Portada:</strong> Haz clic en la estrella ⭐ de una foto para establecerla como portada del álbum</li>
                  <li>• <strong>Galería:</strong> Las fotos se mostrarán organizadas por álbum en la página de galería</li>
                  <li>• Recomendamos imágenes de al menos 800x600 píxeles para mejor calidad</li>
                  <li>• Formatos soportados: JPG, PNG, WebP</li>
                </ul>
              </div>
            </div>

            {/* Gestión de Eventos de la Generación */}
            <div className="glass-card rounded-3xl p-8 mt-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-zuno-wine flex items-center gap-3">
                  <Calendar className="h-7 w-7" />
                  Eventos de la Generación
                </h2>
                <Button
                  variant="outline"
                  onClick={() => navigate("/eventos")}
                  size="sm"
                  data-testid="ver-eventos-page"
                >
                  Ver Página Pública
                </Button>
              </div>

              {/* Crear Nuevo Evento */}
              <Card className="p-6 mb-6 bg-blue-50 border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  Crear Nuevo Evento
                </h3>
                <form onSubmit={crearEvento} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Título del Evento *</label>
                      <Input
                        type="text"
                        value={newEvento.titulo}
                        onChange={(e) => setNewEvento({ ...newEvento, titulo: e.target.value })}
                        placeholder="Ej: XV Aniversario Generación 93-96"
                        data-testid="evento-titulo-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Fecha *</label>
                      <Input
                        type="date"
                        value={newEvento.fecha}
                        onChange={(e) => setNewEvento({ ...newEvento, fecha: e.target.value })}
                        data-testid="evento-fecha-input"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Lugar *</label>
                      <Input
                        type="text"
                        value={newEvento.lugar}
                        onChange={(e) => setNewEvento({ ...newEvento, lugar: e.target.value })}
                        placeholder="Ej: Hotel Fiesta Americana, Guadalajara"
                        data-testid="evento-lugar-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">URL de Imagen (opcional)</label>
                      <Input
                        type="text"
                        value={newEvento.imagen_url}
                        onChange={(e) => setNewEvento({ ...newEvento, imagen_url: e.target.value })}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        data-testid="evento-imagen-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Descripción</label>
                    <textarea
                      value={newEvento.descripcion}
                      onChange={(e) => setNewEvento({ ...newEvento, descripcion: e.target.value })}
                      placeholder="Describe el evento..."
                      rows={3}
                      className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="evento-descripcion-input"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newEvento.destacado}
                        onChange={(e) => setNewEvento({ ...newEvento, destacado: e.target.checked })}
                        className="w-4 h-4 text-blue-600"
                        data-testid="evento-destacado-checkbox"
                      />
                      <span className="text-sm font-medium">Marcar como "Próximo Evento" (destacado)</span>
                    </label>
                  </div>
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={creatingEvento}
                    data-testid="crear-evento-btn"
                  >
                    {creatingEvento ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Creando...
                      </>
                    ) : (
                      <>
                        <Calendar size={16} className="mr-2" />
                        Crear Evento
                      </>
                    )}
                  </Button>
                </form>
              </Card>

              {/* Lista de Eventos */}
              <h3 className="text-lg font-semibold text-slate-700 mb-4">Eventos Registrados ({eventosGeneracion.length})</h3>
              
              {eventosGeneracion.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No hay eventos registrados.</p>
                  <p className="text-sm">Agrega eventos históricos o el próximo evento usando el formulario de arriba.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {eventosGeneracion.map((evento) => (
                    <Card key={evento.id} className={`p-4 ${evento.destacado ? 'border-2 border-blue-500 bg-blue-50' : ''}`} data-testid={`evento-admin-${evento.id}`}>
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* Event Image */}
                        <div className="w-full md:w-48 flex-shrink-0">
                          <div className="aspect-video relative bg-slate-100 rounded-lg overflow-hidden">
                            {evento.imagen_url ? (
                              <img 
                                src={evento.imagen_url} 
                                alt={evento.titulo}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-400">
                                <ImageIcon size={32} />
                              </div>
                            )}
                          </div>
                          {/* Change image button/input */}
                          {editingEventoImagen === evento.id ? (
                            <div className="mt-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => actualizarImagenEvento(evento.id, e.target.files[0])}
                                className="w-full text-xs"
                                disabled={uploadingEventoImagen}
                              />
                              {uploadingEventoImagen && (
                                <p className="text-xs text-blue-600 mt-1">Subiendo...</p>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingEventoImagen(null)}
                                className="mt-1 text-xs"
                              >
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingEventoImagen(evento.id)}
                              className="mt-2 w-full text-xs"
                              data-testid={`cambiar-imagen-evento-${evento.id}`}
                            >
                              <Upload size={12} className="mr-1" />
                              {evento.imagen_url ? 'Cambiar Imagen' : 'Añadir Imagen'}
                            </Button>
                          )}
                        </div>
                        
                        {/* Event Info */}
                        <div className="flex-1">
                          {editingEvento === evento.id ? (
                            // Modo edición
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Título</label>
                                <Input
                                  value={editEventoData.titulo}
                                  onChange={(e) => setEditEventoData({ ...editEventoData, titulo: e.target.value })}
                                  className="text-sm"
                                  placeholder="Título del evento"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Lugar</label>
                                <Input
                                  value={editEventoData.lugar}
                                  onChange={(e) => setEditEventoData({ ...editEventoData, lugar: e.target.value })}
                                  className="text-sm"
                                  placeholder="Lugar del evento"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Descripción</label>
                                <textarea
                                  value={editEventoData.descripcion}
                                  onChange={(e) => setEditEventoData({ ...editEventoData, descripcion: e.target.value })}
                                  className="w-full text-sm border rounded-md px-3 py-2 min-h-[80px]"
                                  placeholder="Descripción del evento"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => guardarEdicionEvento(evento.id)} className="bg-green-600 hover:bg-green-700">
                                  <Save size={14} className="mr-1" /> Guardar
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEditingEvento}>
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // Modo visualización
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-bold text-lg text-slate-800">{evento.titulo}</h4>
                                {evento.destacado && (
                                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">Próximo Evento</span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => startEditingEvento(evento)}
                                  className="ml-2 text-slate-500 hover:text-slate-700"
                                  data-testid={`editar-evento-${evento.id}`}
                                >
                                  <Pencil size={14} />
                                </Button>
                              </div>
                              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(evento.fecha + "T00:00:00").toLocaleDateString("es-MX", { 
                                    year: "numeric", month: "long", day: "numeric" 
                                  })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin size={14} />
                                  {evento.lugar}
                                </span>
                              </div>
                              {evento.descripcion && (
                                <p className="text-sm text-slate-500 mt-2 line-clamp-2">{evento.descripcion}</p>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2 md:items-end">
                          {/* Vincular álbum */}
                          <select
                            value={evento.album_id || ""}
                            onChange={(e) => vincularAlbumEvento(evento.id, e.target.value)}
                            className="text-sm border rounded px-2 py-1"
                            data-testid={`evento-album-select-${evento.id}`}
                          >
                            <option value="">Sin álbum</option>
                            {albums.map((album) => (
                              <option key={album.id} value={album.id}>
                                📁 {album.titulo}
                              </option>
                            ))}
                          </select>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => eliminarEvento(evento.id)}
                            data-testid={`eliminar-evento-${evento.id}`}
                          >
                            <Trash2 size={14} className="mr-1" /> Eliminar
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Gestión del Directorio */}
            <div className="glass-card rounded-3xl p-8 mt-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                  <Users className="h-7 w-7 text-zuno-wine" />
                  Gestión del Directorio ({stats?.directorio_total || 0})
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDirectorioCollapsed(!directorioCollapsed)}
                    className="text-sm"
                  >
                    {directorioCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    {directorioCollapsed ? "Expandir" : "Colapsar"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open('/directorio', '_blank')}
                    className="text-sm"
                  >
                    Ver Público
                  </Button>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-zuno-gold/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-zuno-gold">{stats?.directorio_exalumnos || 0}</p>
                  <p className="text-xs text-slate-600">Exalumnos</p>
                </div>
                <div className="bg-blue-100 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">{stats?.directorio_maestros || 0}</p>
                  <p className="text-xs text-slate-600">Maestros</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-slate-600">{stats?.directorio_invitados || 0}</p>
                  <p className="text-xs text-slate-600">Invitados</p>
                </div>
                <div className="bg-zuno-wine/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-zuno-wine">{stats?.directorio_total || 0}</p>
                  <p className="text-xs text-slate-600">Total</p>
                </div>
              </div>

              {!directorioCollapsed && (
                <>
                  {/* Formulario para agregar */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <h3 className="font-semibold text-slate-700 mb-3">Agregar Nuevo Registro</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                      <Input
                        placeholder="Nombre completo *"
                        value={newDirectorioEntry.nombre}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, nombre: e.target.value})}
                      />
                      <select
                        className="border rounded-lg px-3 py-2 text-sm"
                        value={newDirectorioEntry.tipo}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, tipo: e.target.value})}
                      >
                        <option value="Exalumno 93-96">Exalumno 93-96</option>
                        <option value="Maestro">Maestro</option>
                        <option value="Invitado no Graduado">Invitado no Graduado</option>
                        <option value="Invitado Otra generacion">Invitado Otra Generación</option>
                      </select>
                      <select
                        className="border rounded-lg px-3 py-2 text-sm"
                        value={newDirectorioEntry.area}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, area: e.target.value})}
                      >
                        <option value="">Área (opcional)</option>
                        <option value="Medicina">Medicina</option>
                        <option value="Humanidades">Humanidades</option>
                        <option value="Arquitectura">Arquitectura</option>
                        <option value="Ingenieria">Ingeniería</option>
                        <option value="Administracion">Administración</option>
                      </select>
                      <Input
                        placeholder="Apodo (opcional)"
                        value={newDirectorioEntry.apodo}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, apodo: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                      <Input
                        placeholder="Teléfono"
                        value={newDirectorioEntry.telefono}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, telefono: e.target.value})}
                      />
                      <Input
                        placeholder="Email"
                        value={newDirectorioEntry.email}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, email: e.target.value})}
                      />
                      <Input
                        placeholder="Facebook URL"
                        value={newDirectorioEntry.facebook}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, facebook: e.target.value})}
                      />
                      <Input
                        placeholder="Cumpleaños (ej: 15 de Mayo)"
                        value={newDirectorioEntry.cumpleanos}
                        onChange={(e) => setNewDirectorioEntry({...newDirectorioEntry, cumpleanos: e.target.value})}
                      />
                    </div>
                    <Button onClick={agregarDirectorioEntry} className="bg-zuno-wine hover:bg-zuno-wine/90">
                      Agregar al Directorio
                    </Button>
                  </div>

                  {/* Búsqueda */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Buscar por nombre, apodo o área..."
                      value={directorioSearch}
                      onChange={(e) => setDirectorioSearch(e.target.value)}
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-500 self-center">
                      {filteredDirectorio.length} de {directorioEntries.length}
                    </span>
                  </div>

                  {/* Lista de registros */}
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {filteredDirectorio.slice(0, 50).map((entry) => (
                      <div key={entry.id} className="bg-white rounded-lg p-3 border flex flex-wrap items-center gap-2">
                        {editingDirectorio?.id === entry.id ? (
                          // Modo edición
                          <div className="w-full space-y-2">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              <Input
                                placeholder="Nombre"
                                value={editingDirectorio.nombre}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, nombre: e.target.value})}
                              />
                              <select
                                className="border rounded-lg px-2 py-1 text-sm"
                                value={editingDirectorio.tipo}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, tipo: e.target.value})}
                              >
                                <option value="Exalumno 93-96">Exalumno 93-96</option>
                                <option value="Maestro">Maestro</option>
                                <option value="Invitado no Graduado">Invitado no Graduado</option>
                                <option value="Invitado Otra generacion">Invitado Otra Generación</option>
                              </select>
                              <Input
                                placeholder="Área"
                                value={editingDirectorio.area || ""}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, area: e.target.value})}
                              />
                              <Input
                                placeholder="Apodo"
                                value={editingDirectorio.apodo || ""}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, apodo: e.target.value})}
                              />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              <Input
                                placeholder="Teléfono"
                                value={editingDirectorio.telefono || ""}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, telefono: e.target.value})}
                              />
                              <Input
                                placeholder="Email"
                                value={editingDirectorio.email || ""}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, email: e.target.value})}
                              />
                              <Input
                                placeholder="Facebook"
                                value={editingDirectorio.facebook || ""}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, facebook: e.target.value})}
                              />
                              <Input
                                placeholder="Cumpleaños"
                                value={editingDirectorio.cumpleanos || ""}
                                onChange={(e) => setEditingDirectorio({...editingDirectorio, cumpleanos: e.target.value})}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => actualizarDirectorioEntry(entry.id)} className="bg-green-600 hover:bg-green-700">
                                <Save size={14} className="mr-1" /> Guardar
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingDirectorio(null)}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // Modo vista
                          <>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-800">{entry.nombre}</span>
                                {entry.apodo && <span className="text-slate-500 text-sm">"{entry.apodo}"</span>}
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  entry.tipo === "Exalumno 93-96" ? "bg-zuno-gold/20 text-zuno-gold" :
                                  entry.tipo === "Maestro" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                                }`}>
                                  {entry.tipo}
                                </span>
                                {entry.area && <span className="text-xs text-slate-500">{entry.area}</span>}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                {entry.telefono && <span className="mr-3">{entry.telefono}</span>}
                                {entry.email && <span className="mr-3">{entry.email}</span>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingDirectorio({...entry})}
                                className="h-8 w-8 p-0"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => eliminarDirectorioEntry(entry.id, entry.nombre)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    {filteredDirectorio.length > 50 && (
                      <p className="text-center text-sm text-slate-500 py-2">
                        Mostrando 50 de {filteredDirectorio.length} registros. Usa el buscador para filtrar.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Zona de Peligro - Reset Data */}
            <div className="glass-card rounded-3xl p-8 mt-8 border-2 border-red-300 bg-red-50/50">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-red-700 flex items-center gap-3">
                  <AlertTriangle className="h-7 w-7" />
                  Zona de Peligro
                </h2>
              </div>
              
              <div className="p-4 bg-red-100 rounded-xl border border-red-300 mb-4">
                <h4 className="font-semibold text-red-800 mb-2">⚠️ Reset de Datos para Producción</h4>
                <p className="text-sm text-red-700 mb-4">
                  Esta acción eliminará <strong>TODOS</strong> los datos de prueba:
                </p>
                <ul className="text-sm text-red-700 space-y-1 mb-4">
                  <li>• Compradores y sus boletos</li>
                  <li>• Órdenes SPEI pendientes</li>
                  <li>• Aportaciones</li>
                  <li>• Sponsors (excepto Frevo Eventos)</li>
                  <li>• Estado de boletos de exalumnos</li>
                </ul>
                <p className="text-sm text-red-800 font-semibold">
                  ✓ Se conservará: Sponsor "Frevo Eventos", Álbumes, Fotos, Eventos de la Generación
                </p>
              </div>
              
              <Button
                variant="destructive"
                onClick={resetDataForProduction}
                disabled={resettingData}
                className="bg-red-600 hover:bg-red-700"
                data-testid="reset-data-btn"
              >
                {resettingData ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Reseteando...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} className="mr-2" />
                    Resetear Datos para Producción
                  </>
                )}
              </Button>
            </div>


          </>
        )}

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-zuno-wine hover:bg-zuno-wine/90 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-50"
            data-testid="scroll-to-top-btn"
            title="Ir al inicio"
          >
            <ArrowUp size={24} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
